// The club's weekly training schedule on the public home page, redesigned as
// an interactive planner (see `features/club-home.md`). One layout serves
// every breakpoint — desktop simply gets the same planner wider:
//
//  - a Mon–Sun week strip with prev/next-week chevrons, so a visitor can walk
//    through the calendar. The schedule is weekly-recurring, so every week
//    holds the same sessions; the real dates anchor "today" (outlined only in
//    the week it falls in) and keep the strip an honest calendar. Tapping a
//    day lists its sessions below, grouped into morning / afternoon / evening
//    blocks.
//  - filter chips: by class (each class carries a stable accent colour) and,
//    when the club has marked any class as kids- or adults-only, by audience
//    ("Kids" / "Adults" — an all-ages class matches either, see
//    `matchesAudience`).
//  - session cards: time range with duration, the class name in its accent
//    colour, a Kids/Adults badge where marked, the description, and the
//    coaches with how often the class meets per week.
//
// Everything is derived from the `PublicClubTraining[]` the server already
// sends; week offset, selection and filters are view state only.

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type {
  DayPeriod,
  PublicClubTraining,
  TrainingAudience,
  Weekday,
} from '../../domain';
import {
  DAY_PERIODS,
  WEEKDAYS,
  addDaysIso,
  dayOfMonthIso,
  dayPeriodOf,
  isoDateOf,
  matchesAudience,
  slotDurationMinutes,
  weekOfIso,
  weekdayOfIso,
} from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { accountInitials } from '../labels';
import { publicProfilePath } from './profileSharePath';
import { ACCENT_GRADIENT } from './publicStyles';

/**
 * Accent colours assigned to classes by index, so each class keeps one colour
 * across the strip dots, filter chips and session cards. Picked to read well
 * on the dark public theme and to stay distinct from the green "registration
 * open" and amber "today" accents used elsewhere on the page.
 */
const CLASS_ACCENTS = ['#fb7185', '#c084fc', '#f0b429', '#34d399', '#38bdf8', '#f472b6'];

/** The audience badge/filter colours (kids warm, adults cool). */
const AUDIENCE_COLORS: Record<Exclude<TrainingAudience, 'ALL'>, string> = {
  KIDS: '#f0b429',
  ADULTS: '#38bdf8',
};

const PERIOD_ICONS: Record<DayPeriod, typeof WbTwilightIcon> = {
  MORNING: WbTwilightIcon,
  AFTERNOON: LightModeIcon,
  EVENING: DarkModeIcon,
};

/** One concrete session occurrence: a class meeting at a time on a weekday. */
interface Occurrence {
  classIndex: number;
  startTime: string;
  endTime: string;
}

interface ClubWeekCalendarProps {
  training: PublicClubTraining[];
}

/** The translucent chip look shared by the class and audience filters. */
function filterChipSx(active: boolean, accent: string) {
  return {
    fontWeight: 600,
    color: active ? accent : 'text.secondary',
    bgcolor: active ? `${accent}29` : 'rgba(255,255,255,0.04)',
    border: '1px solid',
    borderColor: active ? `${accent}88` : 'rgba(255,255,255,0.12)',
    '&:hover': { bgcolor: active ? `${accent}3d` : 'rgba(255,255,255,0.08)' },
  } as const;
}

/** A small Kids/Adults badge, shown only when the class is marked. */
function AudienceBadge({ audience }: { audience: TrainingAudience }) {
  const { t } = useTranslation();
  if (audience === 'ALL') return null;
  const color = AUDIENCE_COLORS[audience];
  return (
    <Chip
      size="small"
      label={t(`club.schedule.audience.${audience}`)}
      sx={{
        height: 20,
        fontSize: '0.68rem',
        fontWeight: 700,
        color,
        bgcolor: `${color}24`,
        border: `1px solid ${color}55`,
        flexShrink: 0,
      }}
    />
  );
}

export function ClubWeekCalendar({ training }: ClubWeekCalendarProps) {
  const { t, localeCode } = useTranslation();

  // Real dates anchor the strip: the schedule repeats weekly, but "today" and
  // the day numbers make it feel like a calendar, and the chevrons walk the
  // viewed week backwards or forwards through it.
  const todayIso = isoDateOf(new Date());
  const today = weekdayOfIso(todayIso);

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Weekday>(today);
  const [audienceFilter, setAudienceFilter] = useState<TrainingAudience>('ALL');
  const [classFilter, setClassFilter] = useState<number | null>(null);

  const weekIsoDates = useMemo(
    () => weekOfIso(addDaysIso(todayIso, weekOffset * 7)),
    [todayIso, weekOffset],
  );

  // Bucket every class's weekly slots by weekday, time-sorted, unfiltered —
  // filters are applied per render so the strip can show live match counts.
  const byDay = useMemo(() => {
    const buckets = new Map<Weekday, Occurrence[]>();
    for (const day of WEEKDAYS) buckets.set(day, []);
    training.forEach((cls, classIndex) => {
      for (const slot of cls.schedule) {
        buckets.get(slot.day)?.push({
          classIndex,
          startTime: slot.startTime,
          endTime: slot.endTime,
        });
      }
    });
    for (const day of WEEKDAYS) {
      buckets
        .get(day)
        ?.sort(
          (a, b) =>
            a.startTime.localeCompare(b.startTime) ||
            a.endTime.localeCompare(b.endTime),
        );
    }
    return buckets;
  }, [training]);

  const matches = (occurrence: Occurrence) =>
    matchesAudience(training[occurrence.classIndex].audience, audienceFilter) &&
    (classFilter === null || occurrence.classIndex === classFilter);

  // The filters are offered only when they can change anything: the class
  // filter needs at least two classes, the audience filter at least one
  // kids-/adults-only class.
  const showClassFilter = training.length > 1;
  const showAudienceFilter = training.some((cls) => cls.audience !== 'ALL');
  const filtersActive = audienceFilter !== 'ALL' || classFilter !== null;

  const headingFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }),
    [localeCode],
  );

  // The month/year shown between the week chevrons, taken from the viewed
  // week's Monday so it changes exactly when the strip does.
  const monthFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    [localeCode],
  );

  const accentOf = (classIndex: number) =>
    CLASS_ACCENTS[classIndex % CLASS_ACCENTS.length];

  /** The full session card used in the mobile day list. */
  const sessionCard = (occurrence: Occurrence, key: string) => {
    const cls = training[occurrence.classIndex];
    const accent = accentOf(occurrence.classIndex);
    const minutes = slotDurationMinutes(occurrence);
    const perWeek = cls.schedule.length;
    return (
      <Box
        key={key}
        sx={{
          p: 1.75,
          borderRadius: 2.5,
          background:
            'linear-gradient(165deg, rgba(255,255,255,0.055), rgba(255,255,255,0.015))',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {occurrence.startTime}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            – {occurrence.endTime}
          </Typography>
          {Number.isFinite(minutes) && (
            <Typography variant="caption" color="text.secondary">
              · {t('club.schedule.minutes', { minutes })}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <AudienceBadge audience={cls.audience} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: accent,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: accent, minWidth: 0 }}
            noWrap
          >
            {cls.name}
          </Typography>
        </Stack>
        {cls.description && (
          // The full description travels on the card — the schedule is the
          // only place the public page describes a class.
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
          >
            {cls.description}
          </Typography>
        )}
        {cls.coaches.length > 0 && (
          <>
            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              {cls.coaches.map((coach) => (
                // Each coach links to their public profile page (only coaches
                // whose page is actually public are listed here, so the link
                // always resolves). Their profile photo shows when they have
                // one; initials are the fallback.
                <Stack
                  key={coach.profileToken}
                  component="a"
                  href={publicProfilePath(coach.profileToken)}
                  aria-label={t('club.viewProfile', { name: coach.displayName })}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    minWidth: 0,
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover .coach-name': { textDecoration: 'underline' },
                  }}
                >
                  <Avatar
                    src={coach.photoUrl ?? undefined}
                    alt={coach.displayName}
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: accent,
                      bgcolor: `${accent}26`,
                      border: `1px solid ${accent}3d`,
                    }}
                  >
                    {accountInitials(coach.displayName, '')}
                  </Avatar>
                  <Typography
                    variant="caption"
                    className="coach-name"
                    sx={{ fontWeight: 600 }}
                    noWrap
                  >
                    {coach.displayName}
                  </Typography>
                </Stack>
              ))}
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {t('club.schedule.perWeek', { count: perWeek })}
              </Typography>
            </Stack>
          </>
        )}
      </Box>
    );
  };

  const selectedAll = byDay.get(selectedDay) ?? [];
  const selectedFiltered = selectedAll.filter(matches);
  const selectedIso = weekIsoDates[WEEKDAYS.indexOf(selectedDay)];

  // The mobile day list, grouped into morning / afternoon / evening blocks.
  const periodBlocks = DAY_PERIODS.map((period) => ({
    period,
    items: selectedFiltered.filter(
      (occurrence) => dayPeriodOf(occurrence.startTime) === period,
    ),
  })).filter((block) => block.items.length > 0);

  return (
    <Box>
      {/* Filters: class chips (with their accent dot) and audience chips. */}
      {(showClassFilter || showAudienceFilter) && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ lineHeight: 1.5, flexGrow: 1 }}
            >
              {t('club.schedule.filterTitle')}
            </Typography>
            {filtersActive && (
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                sx={{ fontWeight: 600 }}
                onClick={() => {
                  setAudienceFilter('ALL');
                  setClassFilter(null);
                }}
              >
                {t('club.schedule.reset')}
              </Link>
            )}
          </Stack>
          {showClassFilter && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                clickable
                label={t('club.schedule.allClasses')}
                onClick={() => setClassFilter(null)}
                sx={filterChipSx(classFilter === null, CLASS_ACCENTS[0])}
              />
              {training.map((cls, classIndex) => (
                <Chip
                  key={classIndex}
                  size="small"
                  clickable
                  icon={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: accentOf(classIndex),
                        ml: 0.75,
                      }}
                    />
                  }
                  label={cls.name}
                  onClick={() =>
                    setClassFilter(classFilter === classIndex ? null : classIndex)
                  }
                  sx={filterChipSx(classFilter === classIndex, accentOf(classIndex))}
                />
              ))}
            </Stack>
          )}
          {showAudienceFilter && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {(['ALL', 'KIDS', 'ADULTS'] as const).map((option) => (
                <Chip
                  key={option}
                  size="small"
                  clickable
                  label={t(`club.schedule.audienceFilter.${option}`)}
                  onClick={() => setAudienceFilter(option)}
                  sx={filterChipSx(
                    audienceFilter === option,
                    option === 'ALL' ? '#e2e8f0' : AUDIENCE_COLORS[option],
                  )}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {/* Week navigation: chevrons walk the calendar week by week; the label
          names the viewed week's month (taken from its Monday). */}
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <IconButton
          size="small"
          aria-label={t('club.schedule.prevWeek')}
          onClick={() => setWeekOffset((offset) => offset - 1)}
          sx={{
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 2,
            color: 'text.secondary',
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle2"
          sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 700 }}
        >
          {monthFormat.format(new Date(`${weekIsoDates[0]}T00:00:00Z`))}
        </Typography>
        <IconButton
          size="small"
          aria-label={t('club.schedule.nextWeek')}
          onClick={() => setWeekOffset((offset) => offset + 1)}
          sx={{
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 2,
            color: 'text.secondary',
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* The Mon–Sun strip. Fixed compact type sizes (no theme overline — its
          wide letter-spacing overflowed the seven narrow phone columns). */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: { xs: 0.75, md: 1 },
        }}
      >
        {WEEKDAYS.map((day, index) => {
          const active = day === selectedDay;
          const isToday = weekIsoDates[index] === todayIso;
          const count = (byDay.get(day) ?? []).filter(matches).length;
          return (
            <ButtonBase
              key={day}
              onClick={() => setSelectedDay(day)}
              aria-label={t(`club.weekdays.${day}`)}
              sx={{
                flexDirection: 'column',
                gap: 0.5,
                py: 1,
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: 2,
                border: '1px solid',
                borderColor: active
                  ? 'transparent'
                  : isToday
                    ? 'warning.main'
                    : 'rgba(255,255,255,0.08)',
                backgroundImage: active ? ACCENT_GRADIENT : 'none',
                bgcolor: active ? 'transparent' : 'rgba(255,255,255,0.03)',
                boxShadow: active ? '0 8px 20px rgba(244,63,94,0.30)' : 'none',
                transition: 'background .15s, box-shadow .15s',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: active
                    ? 'rgba(255,255,255,0.85)'
                    : isToday
                      ? 'warning.main'
                      : count > 0
                        ? 'text.secondary'
                        : 'text.disabled',
                }}
              >
                {t(`club.weekdays.${day}`)}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  color: active
                    ? 'common.white'
                    : count > 0
                      ? 'text.primary'
                      : 'text.disabled',
                }}
              >
                {dayOfMonthIso(weekIsoDates[index])}
              </Typography>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor:
                    count > 0
                      ? active
                        ? 'rgba(255,255,255,0.9)'
                        : '#fb7185'
                      : 'transparent',
                }}
              />
            </ButtonBase>
          );
        })}
      </Box>

      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mt: 2, mb: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {headingFormat.format(new Date(`${selectedIso}T00:00:00Z`))}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('club.schedule.sessionCount', { count: selectedFiltered.length })}
        </Typography>
      </Stack>

      {selectedFiltered.length === 0 ? (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: 'center',
            borderRadius: 2.5,
            border: '1px dashed rgba(255,255,255,0.16)',
          }}
        >
          <EventBusyIcon sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedAll.length === 0
              ? t('club.schedule.noSessions')
              : t('club.schedule.noMatches')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {periodBlocks.map(({ period, items }) => {
            const PeriodIcon = PERIOD_ICONS[period];
            return (
              <Box key={period}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PeriodIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4, fontWeight: 700 }}
                  >
                    {t(`club.schedule.period.${period}`)}
                  </Typography>
                  <Divider sx={{ flexGrow: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
                </Stack>
                <Stack spacing={1}>
                  {items.map((occurrence, i) => sessionCard(occurrence, `${period}-${i}`))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
